import { useEffect, useState } from 'react';
import { Modal } from '@/shared/components/Modal';
import { Button } from '@/shared/components/Button';
import {
  useProductivityLoginContext,
  useProductivityLoginMutation,
  useProductivityLoginOptions,
  type ProductivityCode,
} from './useProductivityLogin';

/**
 * PB's **Productivity Tracking Login Change** — ADR-0039 part B (Accepted 2026-08-03).
 *
 * PB window: `ph_system_pop_misc/uo_pop_misc.sru` `of_process_pop_user_productivity` over
 * `dw_pop_user_productivity.srd`, opened with `isparm_open_from_w_frame = 'Y'` from the File-menu
 * leaf `m_file_option3` (`m_main.srm:3441-3530`). Chris's placement ruling (2026-08-03) moves that
 * leaf to the upper-right user profile menu, next to the Workstation picker.
 *
 * Fidelity notes, each verified against the export:
 *
 * - **Four fields, in SRD y-order** — `Warehouse:` (y=88), `Department:` (y=180), `Shift:` (y=276),
 *   `Job:` (y=368) — labels verbatim including the colon (`dw_pop_user_productivity.srd:14,16,18,20`).
 * - **All four are `<select>`, never text boxes.** Every column is a `dddw` with
 *   `dddw.allowedit=no` (`srd:15,17,19,21`), and none carries `edit.displayonly` or a `protect=`
 *   expression — so all four are operable, and none is read-only.
 * - **The options show the code, not the description.** Each dddw sets
 *   `dddw.displaycolumn=<id>` even though its retrieve fetches `description` too, so PB's list
 *   reads `WHS`, not `Warehouse Group`. The description rides along in a `title` tooltip rather
 *   than being spliced into the visible option text.
 * - **The cascade.** Focusing department / shift / job re-`setsqlselect`s that child against the
 *   currently-picked warehouse (`sru:1242-1259`); changing warehouse re-reads the operator's
 *   `usr_warehouse` defaults and **stamps all three child fields with them, even when they are
 *   empty** (`sru:1261-1276` — the `if sqlca.sqlcode = 0` guard is commented out), so a warehouse
 *   change discards manual edits. Both behaviours are ported.
 * - **Cancel is present.** PB hides it only on the forced variant (`sru:1184`); the change variant
 *   keeps it (`sru:1169-1170`).
 * - **The refusal is the server's.** PB's `f_tmessage("Warning","You have to provide complete
 *   Productivity information.")` (`sru:1282-1285`) is raised by the BE as a 422 whose `detail` is
 *   that exact string; this component renders `error.message` verbatim rather than inventing its
 *   own wording, so the two can never drift.
 *
 * **Not built here:** PB's forced post-login variant (`isparm_open_from_w_frame='N'`, Cancel
 * hidden, driven from `f_app_open_connect_after_std.srf:302-403`). Deferred by Chris's ruling; it
 * is dormant on both sides today because `rf_configuration Client/EnableProductivityTrackCRT` is
 * `'N'` in `chris_900`.
 */
export function ProductivityLoginModal({ onClose }: { onClose: () => void }) {
  const context = useProductivityLoginContext();
  const save = useProductivityLoginMutation();

  const [warehouseId, setWarehouseId] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [shiftId, setShiftId] = useState('');
  const [jobId, setJobId] = useState('');
  const [seeded, setSeeded] = useState(false);
  /** Set to the warehouse whose `usr_warehouse` defaults still have to be stamped (PB
   *  `sru:1261-1276`). Null once they have been applied — or when nothing is pending, which is the
   *  state on first open, because PB's change variant seeds from `dbxx.mover`, not `usr_warehouse`. */
  const [pendingDefaultsFor, setPendingDefaultsFor] = useState<string | null>(null);

  // Seed once from the live dbxx.mover row (sru:1177-1180). Guarded by `seeded` rather than by a
  // dependency list so a background refetch of the context query cannot stomp on what the operator
  // has already typed.
  useEffect(() => {
    if (seeded || !context.data) return;
    setWarehouseId(context.data.warehouseId ?? '');
    setDepartmentId(context.data.departmentId ?? '');
    setShiftId(context.data.shiftId ?? '');
    setJobId(context.data.jobId ?? '');
    setSeeded(true);
  }, [context.data, seeded]);

  const options = useProductivityLoginOptions(warehouseId || null, seeded);

  function handleWarehouseChange(next: string) {
    setWarehouseId(next);
    // PB sru:1261-1276: the itemchanged re-reads usr_warehouse and overwrites all three children.
    // We cannot know the new warehouse's defaults until /options answers for it, so clear now and
    // let the effect below stamp whatever comes back — including nothing, which is PB's behaviour
    // when the operator has no usr_warehouse row for that warehouse.
    setDepartmentId('');
    setShiftId('');
    setJobId('');
    setPendingDefaultsFor(next);
  }

  useEffect(() => {
    if (!pendingDefaultsFor || pendingDefaultsFor !== warehouseId) return;
    if (!options.data) return;
    const d = options.data.defaults;
    setDepartmentId(d?.departmentId ?? '');
    setShiftId(d?.shiftId ?? '');
    setJobId(d?.jobId ?? '');
    setPendingDefaultsFor(null);
  }, [options.data, pendingDefaultsFor, warehouseId]);

  function handleSave() {
    save.mutate(
      { warehouseId, departmentId, shiftId, jobId },
      { onSuccess: () => onClose() },
    );
  }

  const loading = context.isLoading;
  const ctx = context.data;

  return (
    <Modal
      open
      onClose={onClose}
      // sru:1169 — of_title("Productivity Tracking Login Change", gs_null), verbatim.
      title="Productivity Tracking Login Change"
      subtitle="Choose the job, department and shift to track"
      size="sm"
      footer={
        <>
          {/* sru:1184 hides Cancel on the FORCED variant only; the change variant keeps it. */}
          <Button variant="secondary" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={loading || save.isPending}>
            {save.isPending ? 'Saving…' : 'OK'}
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-sm">
        <CodeSelect
          label="Warehouse:"
          value={warehouseId}
          options={options.data?.warehouses}
          disabled={loading}
          onChange={handleWarehouseChange}
        />
        <CodeSelect
          label="Department:"
          value={departmentId}
          options={options.data?.departments}
          // Only the initial context load disables these. NOT `!warehouseId`: PB's child dddws
          // stay operable with an empty list (they are only populated on focus,
          // `sru:1242-1259`), and disabling an SRD column that carries neither
          // `edit.displayonly` nor a `protect=` expression would be the 07-31 edit-surface
          // regression all over again.
          disabled={loading}
          onChange={setDepartmentId}
        />
        <CodeSelect
          label="Shift:"
          value={shiftId}
          options={options.data?.shifts}
          // Only the initial context load disables these. NOT `!warehouseId`: PB's child dddws
          // stay operable with an empty list (they are only populated on focus,
          // `sru:1242-1259`), and disabling an SRD column that carries neither
          // `edit.displayonly` nor a `protect=` expression would be the 07-31 edit-surface
          // regression all over again.
          disabled={loading}
          onChange={setShiftId}
        />
        <CodeSelect
          label="Job:"
          value={jobId}
          options={options.data?.jobs}
          // Only the initial context load disables these. NOT `!warehouseId`: PB's child dddws
          // stay operable with an empty list (they are only populated on focus,
          // `sru:1242-1259`), and disabling an SRD column that carries neither
          // `edit.displayonly` nor a `protect=` expression would be the 07-31 edit-surface
          // regression all over again.
          disabled={loading}
          onChange={setJobId}
        />

        {save.isError && (
          // PB f_tmessage("Warning", …) (sru:1283) / f_tmessage("Error", …) (:1295,:1298,
          // m_main.srm:3498). The BE sends the verbatim string as problem+json detail.
          <p role="alert" className="rounded border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-700">
            {save.error instanceof Error ? save.error.message : 'Save failed.'}
          </p>
        )}

        {ctx && !ctx.moverPresent && (
          <p className="text-xs text-amber-700">
            This session has no mover record, so there is nothing to update. Sign out and sign in
            again.
          </p>
        )}

        {ctx && ctx.moverPresent && !ctx.enabled && (
          // PB m_main.srm:3455 gates only the clock; the dbxx.mover write happens either way
          // (:3444-3449). Say so rather than letting the operator assume time is being tracked.
          <p className="text-xs text-slate-500">
            Productivity Tracking is turned off for this client
            (<span className="font-mono">EnableProductivityTrackCRT</span>), so PowerHouse records
            your selection but does not start a clock.
          </p>
        )}

        {ctx?.actSeqNum != null && (
          <p className="text-xs text-slate-500">
            Current activity sequence <span className="font-mono">{ctx.actSeqNum}</span> will be
            closed and a new one started.
          </p>
        )}
      </div>
    </Modal>
  );
}

/**
 * One PB dddw. `dddw.allowedit=no` ⇒ a `<select>`; `dddw.displaycolumn=<id>` ⇒ the option text is
 * the code alone, with the description as a tooltip rather than visible text.
 *
 * The blank `—` option exists because PB's dddw is `nilisnull`-capable and a fresh
 * `dbxx.mover` row genuinely has no department / shift / job, so the control must be able to
 * render "nothing picked" without silently adopting the first option — the
 * `agSelectCellEditor` first-option-corruption class of bug.
 */
function CodeSelect({
  label,
  value,
  options,
  disabled,
  onChange,
}: {
  label: string;
  value: string;
  options: ProductivityCode[] | undefined;
  disabled?: boolean;
  onChange: (next: string) => void;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-slate-600">{label}</span>
      <select
        aria-label={label}
        className="w-full rounded border border-slate-300 px-2 py-1 text-sm disabled:bg-slate-100"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">—</option>
        {(options ?? []).map((o) => (
          <option key={o.id} value={o.id} title={o.description || undefined}>
            {o.id}
          </option>
        ))}
      </select>
    </label>
  );
}
